import type { BettingState, LEG_TYPES, PlaceResult, Updaters } from "@ppb/betslip-core";
import type {
  RunnersMetadata,
  BettingGroup,
  AddLegOptions,
  RunnerMetadataResult,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import type { Entities } from "@ppb/tbd-store/state/entities";
import {
  AddSelectionByGroup,
  createBettingRunnersMetadataSelector,
  createGetAddOneLinePayloadSelector,
  createGetAddPayloadSelector,
  createGetAddSelectionsPayloadSelector,
  createGetMarketRunnerIdAssociationSelector,
  createGetMarketRunnerURNAssociationSelector,
  createGetVirtualAddOneLinePayloadSelector,
  createGetVirtualAddPayloadSelector,
  createGetVirtualAddSelectionsPayloadSelector,
  createGetVirtualMarketRunnerIdAssociationSelector,
  createGetVirtualMarketRunnerURNAssociationSelector,
  createSportsbookRunnerMetadataBuilder,
  createVirtualBettingRunnersMetadataSelector,
  createVirtualRunnerMetadataBuilder,
  MarketRunnerIdAssociation,
  MarketRunnerURNAssociation,
  RunnerMetadataKey,
} from "@ppb/tbd-store/state/entities/entities-selectors";
import type { URN } from "../../../types";
import type { BetslipApplicationState } from "../BetslipApplicationState";

type BettingResolver = {
  getMarketRunnerIdAssociation: (entities: Entities, urn: URN) => MarketRunnerIdAssociation | undefined;
  getMarketRunnerURNAssociation: (
    entities: Entities,
    idAssociationMap: MarketRunnerIdAssociation[],
  ) => MarketRunnerURNAssociation[];
  getAddLegPayload: (entities: Entities, urn: URN, legOptions?: AddLegOptions) => Updaters.AddLegPayload | undefined;
  getAddOneLinePayload: (
    runners: BettingState.Runner[],
    legType: LEG_TYPES,
    legOptions?: AddLegOptions,
  ) => Updaters.AddLegPayload | undefined;
  getAddSelectionsPayload: (entities: Entities, report: PlaceResult.PlacedBetResults) => AddSelectionByGroup;
  getMetadata: (state: BetslipApplicationState) => RunnersMetadata;
  getRunnerMetadata: (entities: Entities, runner: RunnerMetadataKey) => RunnerMetadataResult;
};

type BettingResolverMap = Record<BettingGroup, BettingResolver>;

const buildSportsbookRunnerMetadata = createSportsbookRunnerMetadataBuilder();

const BETTING_RESOLVER_MAP: BettingResolverMap = {
  VIRTUAL: {
    getMarketRunnerIdAssociation: createGetVirtualMarketRunnerIdAssociationSelector(),
    getMarketRunnerURNAssociation: createGetVirtualMarketRunnerURNAssociationSelector(),
    getAddLegPayload: createGetVirtualAddPayloadSelector(),
    getAddOneLinePayload: createGetVirtualAddOneLinePayloadSelector,
    getAddSelectionsPayload: createGetVirtualAddSelectionsPayloadSelector(),
    getMetadata: createVirtualBettingRunnersMetadataSelector(),
    getRunnerMetadata: createVirtualRunnerMetadataBuilder(),
  },
  REAL: {
    getMarketRunnerIdAssociation: createGetMarketRunnerIdAssociationSelector(),
    getMarketRunnerURNAssociation: createGetMarketRunnerURNAssociationSelector(),
    getAddLegPayload: createGetAddPayloadSelector(),
    getAddOneLinePayload: createGetAddOneLinePayloadSelector,
    getAddSelectionsPayload: createGetAddSelectionsPayloadSelector(),
    getMetadata: createBettingRunnersMetadataSelector(),
    getRunnerMetadata: (entities, runner) => buildSportsbookRunnerMetadata(entities, runner, false),
  },
};

export const getBettingResolvers = (group: BettingGroup = "REAL"): BettingResolver => BETTING_RESOLVER_MAP[group];
